// client/src/components/admin/AdminVatCreditBidDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const AdminVatCreditBidDetails = () => {
  const { requestId, bidId } = useParams();
  const navigate = useNavigate();
  const [bid, setBid] = useState(null);
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBidDetails = async () => {
      try {
        // No per-bid endpoint exists for VAT credit bids: the request detail
        // endpoint already returns every bid (bank/createdBy/allocatedBy populated).
        const res = await axios.get(`/api/vat-credit-requests/${requestId}`);

        if (res.data && res.data.request) {
          setRequest(res.data.request);

          const foundBid = (res.data.bids || []).find(
            b => (b.id || b._id) === bidId
          );

          if (foundBid) {
            setBid(foundBid);
          } else {
            setError('Bid not found');
          }
        } else {
          setError('Invalid response format from server');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching VAT credit bid:', err);
        setError('Failed to fetch bid details');
        setLoading(false);
      }
    };

    fetchBidDetails();
  }, [requestId, bidId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    if (amount === undefined || amount === null) return 'N/A';
    return `${currency} ${(Number(amount) || 0).toLocaleString('en-US')}`;
  };

  if (loading) {
    return <div className="loading">Loading bid details...</div>;
  }

  if (error && !bid) {
    return <div className="error-message">{error}</div>;
  }

  if (!bid) {
    return <div className="error-message">Bid not found</div>;
  }

  const requestCurrency = request?.currency || 'EUR';
  const displayBidId = bid._id || bid.id;

  return (
    <div className="admin-container">
      <div className="admin-header">
        <button
          onClick={() => navigate(-1)}
          className="btn btn-light"
        >
          &larr; Back
        </button>
        <h2 className="admin-page-title">VAT Credit Bid Details #{displayBidId}</h2>
      </div>

      {error && <div className="alert-danger">{error}</div>}

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Bid Information</h3>
          <span className={`status status-${bid.status === 'approved' ? 'approved' : bid.status === 'rejected' ? 'rejected' : bid.status === 'cancelled' ? 'cancelled' : 'pending'}`}>
            {bid.status || 'pending'}
          </span>
        </div>
        <div className="admin-card-body">
          <div className="admin-detail-row">
            <div className="admin-detail-label">Bid ID</div>
            <div className="admin-detail-value">#{displayBidId}</div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Bank</div>
            <div className="admin-detail-value">
              {bid.bank ? (
                <Link to={`/admin/organizations/${bid.bank._id || bid.bank.id || ''}`}>
                  {bid.bank.name || 'View Bank'}
                </Link>
              ) : 'N/A'}
            </div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Desired Amount</div>
            <div className="admin-detail-value">{formatCurrency(bid.desiredAmount, requestCurrency)}</div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Rate Type</div>
            <div className="admin-detail-value">{bid.rateType === 'floating' ? 'Floating' : 'Fixed'}</div>
          </div>

          {bid.rateType === 'floating' && bid.baseRate && (
            <div className="admin-detail-row">
              <div className="admin-detail-label">Base Rate</div>
              <div className="admin-detail-value">{bid.baseRate.replace('_', ' ').toUpperCase()}</div>
            </div>
          )}

          <div className="admin-detail-row">
            <div className="admin-detail-label">Interest Rate</div>
            <div className="admin-detail-value">{bid.interestRate || 0}%</div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Commission</div>
            <div className="admin-detail-value">{bid.commission != null ? `${bid.commission}%` : 'N/A'}</div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Official Days</div>
            <div className="admin-detail-value">{bid.indicativeDays}</div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Transfer Notification</div>
            <div className="admin-detail-value">
              {bid.transferNotification === 'notified' ? 'Notified' : 'Not Notified'}
            </div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Allocated Amount</div>
            <div className="admin-detail-value">
              {bid.allocatedAmount ? formatCurrency(bid.allocatedAmount, requestCurrency) : 'Not allocated'}
            </div>
          </div>

          {bid.allocatedBy && (
            <div className="admin-detail-row">
              <div className="admin-detail-label">Allocated By</div>
              <div className="admin-detail-value">
                {bid.allocatedBy.firstName} {bid.allocatedBy.lastName}
              </div>
            </div>
          )}

          <div className="admin-detail-row">
            <div className="admin-detail-label">Submitted By</div>
            <div className="admin-detail-value">
              {bid.createdBy ? `${bid.createdBy.firstName} ${bid.createdBy.lastName}` : 'N/A'}
            </div>
          </div>

          <div className="admin-detail-row">
            <div className="admin-detail-label">Submission Date</div>
            <div className="admin-detail-value">{formatDate(bid.createdAt)}</div>
          </div>

          {bid.notes && (
            <div className="admin-detail-row">
              <div className="admin-detail-label">Notes</div>
              <div className="admin-detail-value">{bid.notes}</div>
            </div>
          )}
        </div>
      </div>

      {request && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Request Information</h3>
            <Link to={`/admin/vat-credit-requests/${request._id || request.id}`} className="admin-view-btn">
              View Request
            </Link>
          </div>
          <div className="admin-card-body">
            <div className="admin-detail-row">
              <div className="admin-detail-label">Request ID</div>
              <div className="admin-detail-value">#{request.requestId || 'N/A'}</div>
            </div>

            <div className="admin-detail-row">
              <div className="admin-detail-label">Company</div>
              <div className="admin-detail-value">
                {request.company ? (
                  <Link to={`/admin/organizations/${request.company._id || request.company.id || ''}`}>
                    {request.company.name || 'View Company'}
                  </Link>
                ) : 'N/A'}
              </div>
            </div>

            <div className="admin-detail-row">
              <div className="admin-detail-label">Request Amount</div>
              <div className="admin-detail-value">{formatCurrency(request.amount, requestCurrency)}</div>
            </div>

            <div className="admin-detail-row">
              <div className="admin-detail-label">Status</div>
              <div className="admin-detail-value">
                <span className={`status status-${request.status || 'unknown'}`}>
                  {(request.status || 'unknown').replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVatCreditBidDetails;
